/**
 * Location utility functions for parsing and managing location data
 */

/**
 * Parse Google Maps address components into structured location data
 * @param {Array} addressComponents - Array of address components from Google Maps API
 * @returns {Object} Structured location object with city, state, country
 */
export const parseAddressComponents = (addressComponents) => {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return null;
  }

  const location = {
    city: null,
    state: null,
    stateCode: null,
    country: null,
    countryCode: null,
  };

  addressComponents.forEach((component) => {
    const types = component.types;

    // City (locality)
    if (types.includes('locality')) {
      location.city = component.long_name;
    }
    // State (administrative_area_level_1)
    else if (types.includes('administrative_area_level_1')) {
      location.state = component.long_name;
      location.stateCode = component.short_name;
    }
    // Country
    else if (types.includes('country')) {
      location.country = component.long_name;
      location.countryCode = component.short_name;
    }
  });

  return location;
};

/**
 * Extract location from a Google Place object
 * @param {Object} place - Google Place object
 * @returns {Object} Structured location object
 */
export const extractLocationFromPlace = (place) => {
  if (!place || !place.address_components) {
    return null;
  }

  return parseAddressComponents(place.address_components);
};

/**
 * Determine list location by analyzing all places in the list
 * Logic:
 * - If all places are in the same city → show "City, ST"
 * - If multiple cities but same state → show "State"
 * - If multiple states but same country → show "Country"
 * - If multiple countries → show "Global"
 * - If no places → return null
 *
 * @param {Array} places - Array of place objects with addressComponents
 * @returns {Object|null} Location object with display string, or null if no places
 */
export const determineListLocation = (places) => {
  // If no places, return null
  if (!places || places.length === 0) {
    return null;
  }

  // Parse locations from all places
  const locations = places
    .map(place => {
      if (place.addressComponents) {
        return parseAddressComponents(place.addressComponents);
      }
      return null;
    })
    .filter(loc => loc !== null);

  // If no valid locations, return null
  if (locations.length === 0) {
    return null;
  }

  // Get unique values at each level
  const uniqueCities = new Set(locations.map(loc => loc.city).filter(Boolean));
  const uniqueStates = new Set(locations.map(loc => loc.stateCode || loc.state).filter(Boolean));
  const uniqueCountries = new Set(locations.map(loc => loc.countryCode || loc.country).filter(Boolean));

  // Use the first location for reference data
  const firstLocation = locations[0];

  // All in same city
  if (uniqueCities.size === 1) {
    const city = Array.from(uniqueCities)[0];
    const stateCode = firstLocation.stateCode || firstLocation.state;
    return {
      display: `${city}, ${stateCode}`,
      city: city,
      state: firstLocation.state,
      stateCode: firstLocation.stateCode,
      country: firstLocation.country,
      countryCode: firstLocation.countryCode,
    };
  }

  // Multiple cities, but same state
  if (uniqueStates.size === 1) {
    return {
      display: firstLocation.state || firstLocation.stateCode,
      city: null,
      state: firstLocation.state,
      stateCode: firstLocation.stateCode,
      country: firstLocation.country,
      countryCode: firstLocation.countryCode,
    };
  }

  // Multiple states, but same country
  if (uniqueCountries.size === 1) {
    return {
      display: firstLocation.country || firstLocation.countryCode,
      city: null,
      state: null,
      stateCode: null,
      country: firstLocation.country,
      countryCode: firstLocation.countryCode,
    };
  }

  // Multiple countries
  return {
    display: 'Global',
    city: null,
    state: null,
    stateCode: null,
    country: null,
    countryCode: null,
  };
};

/**
 * Determine list category by analyzing all places in the list
 * Logic:
 * - If all places have the same category → use that category
 * - If categories differ → use "Mixed Categories"
 * - If no places → return null
 *
 * @param {Array} places - Array of place objects with category field
 * @returns {string|null} Category string, or null if no places
 */
export const determineListCategory = (places) => {
  // If no places, return null
  if (!places || places.length === 0) {
    return null;
  }

  // Get all non-null categories
  const categories = places
    .map(place => place.category)
    .filter(cat => cat != null && cat !== '');

  // If no valid categories, return null
  if (categories.length === 0) {
    return null;
  }

  // Get unique categories
  const uniqueCategories = new Set(categories);

  // If all the same, return that category
  if (uniqueCategories.size === 1) {
    return Array.from(uniqueCategories)[0];
  }

  // Mixed categories
  return 'Mixed Categories';
};

/**
 * Update list location based on new place location
 * @deprecated Use determineListLocation instead for more accurate results
 *
 * This function is kept for backward compatibility but has issues:
 * - Uses incremental updates rather than analyzing all places
 * - Results depend on the order places are added
 *
 * @param {Object|null} currentListLocation - Current list location object
 * @param {Object} newPlaceLocation - New place location object
 * @returns {Object} Updated list location object with display string
 */
export const updateListLocation = (currentListLocation, newPlaceLocation) => {
  if (!newPlaceLocation) {
    return currentListLocation;
  }

  // If list has no location or has the placeholder text, set to "City, State"
  if (!currentListLocation ||
      !currentListLocation.city ||
      currentListLocation.display === 'Add a place to set location') {
    return {
      display: `${newPlaceLocation.city || 'Unknown'}, ${newPlaceLocation.stateCode || newPlaceLocation.state || 'Unknown'}`,
      city: newPlaceLocation.city,
      state: newPlaceLocation.state,
      stateCode: newPlaceLocation.stateCode,
      country: newPlaceLocation.country,
      countryCode: newPlaceLocation.countryCode,
    };
  }

  // Check if city matches
  if (currentListLocation.city === newPlaceLocation.city) {
    // No change needed
    return currentListLocation;
  }

  // City differs - check state
  if (currentListLocation.stateCode === newPlaceLocation.stateCode ||
      currentListLocation.state === newPlaceLocation.state) {
    // Different city, same state → Update to show state only
    return {
      display: currentListLocation.state || currentListLocation.stateCode,
      city: null, // Clear city since we're showing state level
      state: currentListLocation.state,
      stateCode: currentListLocation.stateCode,
      country: currentListLocation.country,
      countryCode: currentListLocation.countryCode,
    };
  }

  // State differs - check country
  if (currentListLocation.countryCode === newPlaceLocation.countryCode ||
      currentListLocation.country === newPlaceLocation.country) {
    // Different state, same country → Update to show country only
    return {
      display: currentListLocation.country || currentListLocation.countryCode,
      city: null,
      state: null,
      stateCode: null,
      country: currentListLocation.country,
      countryCode: currentListLocation.countryCode,
    };
  }

  // Country differs → Set to "Global"
  return {
    display: 'Global',
    city: null,
    state: null,
    stateCode: null,
    country: null,
    countryCode: null,
  };
};

/**
 * Format a location override object for display
 * Handles city, state, and country level overrides
 * @param {Object|null} locationOverride - Location override object
 * @returns {string} Formatted location string, or empty string if no override
 */
export const formatLocationOverride = (locationOverride) => {
  if (!locationOverride) return '';

  // City level: "City, ST"
  if (locationOverride.city) {
    const stateCode = locationOverride.stateCode || locationOverride.state;
    return stateCode ? `${locationOverride.city}, ${stateCode}` : locationOverride.city;
  }

  // State level: "State" or "ST"
  if (locationOverride.state || locationOverride.stateCode) {
    return locationOverride.state || locationOverride.stateCode;
  }

  // Country level: "Country"
  if (locationOverride.country || locationOverride.countryCode) {
    return locationOverride.country || locationOverride.countryCode;
  }

  return '';
};

/**
 * Format location for display
 * @param {Object|string} location - Location object or string
 * @returns {string} Formatted location string
 */
export const formatLocationDisplay = (location) => {
  if (!location) {
    return '';
  }

  // If it's already a string (legacy data), return as-is
  if (typeof location === 'string') {
    return location;
  }

  // If it's an object with display property, return that
  if (location.display) {
    return location.display;
  }

  // Fallback: construct from available data
  if (location.city && location.stateCode) {
    return `${location.city}, ${location.stateCode}`;
  }
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`;
  }
  if (location.state) {
    return location.state;
  }
  if (location.country) {
    return location.country;
  }

  return '';
};

/**
 * Format a place address to show only street, city, and state (no zip, no country)
 * @param {string} formattedAddress - Full address string (e.g., "309 Clement St, San Francisco, CA 94118, USA")
 * @returns {string} Formatted address with only street, city, state (e.g., "309 Clement St, San Francisco, CA")
 */
export const formatPlaceAddress = (formattedAddress) => {
  if (!formattedAddress || typeof formattedAddress !== 'string') {
    return '';
  }

  // Split by commas
  const parts = formattedAddress.split(',').map(part => part.trim());

  if (parts.length === 0) {
    return '';
  }

  // Build the address with street, city, and state only
  const result = [];

  // Part 0: Street address
  if (parts[0]) {
    result.push(parts[0]);
  }

  // Part 1: City
  if (parts[1]) {
    result.push(parts[1]);
  }

  // Part 2: Extract state code (before any numbers/zip)
  if (parts[2]) {
    const stateMatch = parts[2].match(/^([A-Z]{2})/);
    if (stateMatch) {
      result.push(stateMatch[1]);
    }
  }

  return result.join(', ');
};

/**
 * Split a place address into street and city/state for two-line display
 * @param {string} formattedAddress - Full address string (e.g., "309 Clement St, San Francisco, CA 94118, USA")
 * @param {Array} addressComponents - Optional address components from Google Maps API
 * @returns {Object} Object with street and cityState properties
 */
export const splitPlaceAddress = (formattedAddress, addressComponents = null) => {
  if (!formattedAddress || typeof formattedAddress !== 'string') {
    return { street: '', cityState: '' };
  }

  // If we have address components, use them for accurate parsing
  if (addressComponents && Array.isArray(addressComponents)) {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';

    addressComponents.forEach((component) => {
      const types = component.types;

      // Street number
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      // Street name
      else if (types.includes('route')) {
        route = component.long_name;
      }
      // City
      else if (types.includes('locality')) {
        city = component.long_name;
      }
      // State (use short_name for 2-letter code)
      else if (types.includes('administrative_area_level_1')) {
        state = component.short_name;
      }
    });

    // Build street address (street number + route)
    const street = [streetNumber, route].filter(Boolean).join(' ');

    // Build city, state line
    const cityState = [city, state].filter(Boolean).join(', ');

    return { street, cityState };
  }

  // Fallback to old logic if no address components available
  // Split by commas
  const parts = formattedAddress.split(',').map(part => part.trim());

  if (parts.length === 0) {
    return { street: '', cityState: '' };
  }

  // Part 0: Street address
  const street = parts[0] || '';

  // Part 1: City
  const city = parts[1] || '';

  // Part 2: Extract state code (before any numbers/zip)
  let state = '';
  if (parts[2]) {
    const stateMatch = parts[2].match(/^([A-Z]{2})/);
    if (stateMatch) {
      state = stateMatch[1];
    }
  }

  // Combine city and state
  const cityState = city && state ? `${city}, ${state}` : (city || state);

  return { street, cityState };
};
